/**
 * @file MlrFormatToolbar.tsx
 * @brief Word-style B / I / U / list controls for the focused MLR paragraph.
 *
 * @author DES
 * @date 08-09-2026
 */

import { useEffect, useState, type ReactNode } from "react";
import { Bold, Italic, List, ListOrdered, Underline } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { useMlrRichText } from "./mlrRichTextContext";

function FormatButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm",
        disabled
          ? "text-muted-foreground/50"
          : active
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function MlrFormatToolbar() {
  const { editor } = useMlrRichText();
  const [, setTick] = useState(0);
  const disabled = !editor;

  useEffect(() => {
    if (!editor) return;
    const update = () => setTick((n) => n + 1);
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  return (
    <div
      data-mlr-format-toolbar
      className="flex items-center gap-0.5"
      role="toolbar"
      aria-label="Text formatting"
    >
      <FormatButton
        label="Bold"
        active={editor?.isActive("bold") === true}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <Bold className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        label="Italic"
        active={editor?.isActive("italic") === true}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        label="Underline"
        active={editor?.isActive("underline") === true}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
      >
        <Underline className="h-3.5 w-3.5" />
      </FormatButton>
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      <FormatButton
        label="Bullet list"
        active={editor?.isActive("bulletList") === true}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      >
        <List className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        label="Numbered list"
        active={editor?.isActive("orderedList") === true}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </FormatButton>
    </div>
  );
}
